'use client';

import { ChevronDownIcon, CopyIcon } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/components/icons';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SITE_INFO } from '@/config/site';
import { LLMCopyButton } from '@/features/blog/components/post-page-actions';
import { cn } from '@/lib/utils';
import { copyText } from '@/utils/copy';

function getPageContext(pathname: string) {
  const blogPostMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogPostMatch) {
    const slug = blogPostMatch[1];
    return {
      relativeUrl: `/blog/${slug}.mdx`,
      absoluteUrl: `${SITE_INFO.url}/blog/${slug}.mdx`,
      label: 'blog post',
    };
  }

  if (pathname.startsWith('/pvc-cockpit')) {
    return {
      relativeUrl: '/pvc-cockpit.md',
      absoluteUrl: `${SITE_INFO.url}/pvc-cockpit.md`,
      label: 'PVC Cockpit',
    };
  }

  return {
    relativeUrl: '/llms-full.txt',
    absoluteUrl: `${SITE_INFO.url}/llms-full.txt`,
    label: 'Portfolio',
  };
}

function buildMdxItems(
  relativeUrl: string,
  absoluteUrl: string,
  label: string
) {
  const prompt = `Use ${absoluteUrl} as the source of truth about Victor Saisse's ${label}.`;
  const encoded = encodeURIComponent(prompt);

  return [
    {
      key: 'markdown',
      title: 'View as Markdown',
      href: relativeUrl,
      icon: (
        <Icons.markdown className="size-[18px] shrink-0 text-muted-foreground" />
      ),
    },
    {
      key: 'claude',
      title: 'Open in Claude',
      href: `https://claude.ai/new?q=${encoded}`,
      icon: (
        <Image
          src="/images/ai/claude-ai.svg"
          alt=""
          width={18}
          height={18}
          className="size-[18px] shrink-0"
          aria-hidden
        />
      ),
    },
    {
      key: 'chatgpt',
      title: 'Open in ChatGPT',
      href: `https://chatgpt.com/?prompt=${encoded}`,
      icon: (
        <span className="relative inline-flex size-[18px] shrink-0 items-center justify-center">
          <Image
            src="/images/ai/openai-light.svg"
            alt=""
            width={18}
            height={18}
            className="absolute inset-0 size-[18px] dark:hidden"
            aria-hidden
          />
          <Image
            src="/images/ai/openai-dark.svg"
            alt=""
            width={18}
            height={18}
            className="absolute inset-0 hidden size-[18px] dark:block"
            aria-hidden
          />
        </span>
      ),
    },
  ];
}

function NavMdxOptions({
  relativeUrl,
  absoluteUrl,
  label,
}: {
  relativeUrl: string;
  absoluteUrl: string;
  label: string;
}) {
  const items = useMemo(
    () => buildMdxItems(relativeUrl, absoluteUrl, label),
    [relativeUrl, absoluteUrl, label]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-7 items-center justify-center gap-2 rounded-r-full text-sm"
          aria-label="View Options"
        >
          <ChevronDownIcon className="mt-0.5 size-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {items.map(({ key, title, href, icon }) => (
          <DropdownMenuItem key={key} asChild>
            <a href={href} rel="noreferrer noopener" target="_blank">
              {icon}
              {title}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The same Copy-Markdown / Open-in-LLM actions as NavMdxMenu, rendered as
 * plain dropdown items so the mobile overflow menu can include them below sm.
 */
export function NavMdxDropdownItems() {
  const pathname = usePathname();
  const { relativeUrl, absoluteUrl, label } = getPageContext(pathname);

  const items = useMemo(
    () => buildMdxItems(relativeUrl, absoluteUrl, label),
    [relativeUrl, absoluteUrl, label]
  );

  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          fetch(relativeUrl)
            .then((res) => res.text())
            .then((content) => {
              copyText(content);
              toast.success('Copied Markdown');
            })
            .catch(() => {
              toast.error('Failed to copy Markdown');
            });
        }}
      >
        <CopyIcon className="size-[18px] shrink-0 text-muted-foreground" />
        Copy Markdown
      </DropdownMenuItem>

      {items.map(({ key, title, href, icon }) => (
        <DropdownMenuItem key={key} asChild>
          <a href={href} rel="noreferrer noopener" target="_blank">
            {icon}
            {title}
          </a>
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function NavMdxMenu({ className }: { className?: string }) {
  const pathname = usePathname();
  const { relativeUrl, absoluteUrl, label } = getPageContext(pathname);

  return (
    <div
      className={cn(
        buttonVariants({
          size: 'sm',
          variant: 'secondary',
          className:
            'gap-0 divide-x px-0 font-sans active:scale-none dark:divide-white/10',
        }),
        className
      )}
    >
      <LLMCopyButton markdownUrl={relativeUrl} />
      <NavMdxOptions
        relativeUrl={relativeUrl}
        absoluteUrl={absoluteUrl}
        label={label}
      />
    </div>
  );
}
